import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ButtonComponent,
  FormButtonsComponent,
  InaccessibleDirective,
  LoadingIndicatorService
} from '@icz/angular-essentials';
import {
  FormAutocompleteComponent,
  FormFieldComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators
} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {finalize, startWith} from 'rxjs/operators';
import {ApiCrossReferenceTemplateService, CrossReferenceTemplateDto} from '|api/codebook';
import {CrossReferenceSpecializationType, CrossReferenceType, EntityType, InternalNotificationKey} from '|api/commons';
import {ApiCrossReferenceService, CrossReferenceCreateDto, DocumentDto, FileDto} from '|api/document';
import {
  DocumentOrFileSelectorComponent,
  DocumentSelectorComponent,
  entityTypeAsDocumentOrFile,
  FileSelectorComponent,
  isDocumentEntity,
  NotImplementedComponent
} from '|shared';
import {ReactiveFormsModule} from '@angular/forms';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable,
  namedDtoToOption
} from '@icz/angular-application-core';
import {map} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {
  DocumentToastService,
  DocumentToastType,
  EsslHistoryService,
  FileToastService,
  FileToastType
} from '|ui-essl-core';

export interface CrossReferenceCreateDialogData {
  entityId: number,
  entityType: EntityType
}

enum EntityTypeFilterOptions {
  ALL = 'ALL',
  DOCUMENT = EntityType.DOCUMENT,
  FILE = EntityType.FILE,
}

interface EntityForReferenceOptionData {
  entityType: EntityType;
  entityId: number;
}

type EntityForReferenceOption = IczOption<string, EntityForReferenceOptionData>;

@Component({
  selector: 'icz-cross-reference-create-viewer',
  templateUrl: './cross-reference-create-viewer.component.html',
  styleUrls: ['./cross-reference-create-viewer.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  imports: [ReactiveFormsModule, IczFormContainerDirective, InaccessibleDirective, FormAutocompleteComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, NotImplementedComponent, DocumentOrFileSelectorComponent, DocumentSelectorComponent, FileSelectorComponent, FormFieldComponent, AsyncPipe]
})
export class CrossReferenceCreateViewerComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private apiCrossReferenceTemplateService = inject(ApiCrossReferenceTemplateService);
  private apiCrossReferenceService = inject(ApiCrossReferenceService);
  private documentToastService = inject(DocumentToastService);
  private fileToastService = inject(FileToastService);
  private destroyRef = inject(DestroyRef);
  protected modalData = injectModalData<CrossReferenceCreateDialogData>();
  protected historyService = inject(EsslHistoryService);
  protected cd = inject(ChangeDetectorRef);

  form = new IczFormGroup({
    name: new IczFormControl<Nullable<string>>(null, [IczValidators.required()]),
    entitiesReferringTo: new IczFormControl<Nullable<string[]>>(null, [IczValidators.required()]),
    entityType: new IczFormControl<Nullable<EntityTypeFilterOptions>>(EntityTypeFilterOptions.ALL,  [IczValidators.required()]),
  });

  formGroupsToCheck!: string[];

  templateOptions: IczOption[] = [];
  availableTemplates: CrossReferenceTemplateDto[] = [];

  selectedEntities: EntityForReferenceOption[] = [];

  entityTypeOptions$ = this.nameControl.valueChanges.pipe(
    startWith(null),
    map(name => this.availableTemplates.find(tmpl => tmpl.id === name)),
    map((template): IczOption[] => {
      return [
        {label: 'Všechny objekty', value: EntityTypeFilterOptions.ALL},
        {label: 'Dokumenty', value: EntityTypeFilterOptions.DOCUMENT},
        {label: 'Spisy', value: EntityTypeFilterOptions.FILE}
      ].filter(o => {
        const entityTypesFromTemplate = template?.entityReferringTo ?? [];

        if (entityTypesFromTemplate.length === 1) {
          return entityTypesFromTemplate.includes(o.value as unknown as EntityType);
        }
        else {
          return o.value === EntityTypeFilterOptions.ALL ||
            entityTypesFromTemplate.includes(o.value as unknown as EntityType);
        }
      });
    }),
  );

  allowableEntityTypes: EntityType[] = [];

  get nameControl() {
    return this.form.get('name')!;
  }

  get entityTypeControl() {
    return this.form.get('entityType')!;
  }

  get entitiesReferringToControl() {
    return this.form.get('entitiesReferringTo')!;
  }

  ngOnInit(): void {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.loadingService.doLoading(
      this.apiCrossReferenceTemplateService.crossReferenceTemplateGetByEntityReferringFrom({entityType: this.modalData.entityType}),
      this
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(templates => {
      this.availableTemplates = templates;
      this.templateOptions = templates.map(namedDtoToOption());
    });

    this.nameControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(template => {
      this.entitiesReferringToControl.setValue(null);
      this.allowableEntityTypes = this.getAllowableEntityTypes();

      if (template) {
        const selectedTemplateEntityTypes = this.availableTemplates.find(tmpl => tmpl.id === this.nameControl.value)?.entityReferringTo ?? [];

        if (selectedTemplateEntityTypes.length === 1) {
          this.entityTypeControl.setValue(selectedTemplateEntityTypes[0] as unknown as EntityTypeFilterOptions);
        }
        else {
          this.entityTypeControl.setValue(EntityTypeFilterOptions.ALL);
        }
      }
      else {
        this.entityTypeControl.setValue(EntityTypeFilterOptions.ALL);
      }
    });

    this.entityTypeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        const templateId = this.nameControl.value;
        if (templateId) {
          this.entitiesReferringToControl.setValue(null);
          this.allowableEntityTypes = this.getAllowableEntityTypes();
        }
      }
    });
  }

  objectsSelected(objects: Array<DocumentDto | FileDto>) {
    this.selectedEntities = objects.map(o => ({
      value: `${o.entityType}${o.id}`,
      label: o.subject,
      data: {
        entityId: o.id,
        entityType: o.entityType!,
      }
    }));
  }

  submit() {
    if (this.entitiesReferringToControl.value?.length !== this.selectedEntities.length) {
      throw new Error('SelectedEntities form value and controller value are not in sync.');
    }

    const selectedTemplate = this.availableTemplates.find(tmpl => tmpl.id === this.nameControl.value);

    const requestBody: CrossReferenceCreateDto[] = this.selectedEntities.map(selectedEntity => ({
      entityReferringFromId: this.modalData.entityId,
      entityReferringFromType: entityTypeAsDocumentOrFile(this.modalData.entityType)!,
      entityReferringToId: selectedEntity.data!.entityId,
      entityReferringToType: entityTypeAsDocumentOrFile(selectedEntity.data!.entityType)!,
      name: selectedTemplate!.name,
      roleReferringFrom: selectedTemplate!.roleReferringFrom,
      roleReferringTo: selectedTemplate!.roleReferringTo,
      type: CrossReferenceType.WEAK_BY_USER,
      specialization: CrossReferenceSpecializationType.USER_REFERENCE,
    }));

    this.loadingService.doLoading(
      this.apiCrossReferenceService.crossReferenceCreate({
        templateId: selectedTemplate!.id!,
        body: requestBody,
      }),
      this,
    ).pipe(
      finalize(() => this.modalRef.close(true)),
    ).subscribe(_ => {
      this.selectedEntities.forEach(entity => {
        if (isDocumentEntity(entity.data!.entityType)) {
          this.documentToastService.dispatchDocumentInfoToast(DocumentToastType.CROSS_REFERENCE_DOCUMENT_CREATED, {
            [InternalNotificationKey.DOCUMENT_SUBJECT]: entity.label,
            [InternalNotificationKey.DOCUMENT_ID]: entity.data!.entityId
          });
        } else if (entity.data!.entityType === EntityType.FILE) {
          this.fileToastService.dispatchFileInfoToast(FileToastType.CROSS_REFERENCE_FILE_CREATED, {
            [InternalNotificationKey.FILE_SUBJECT]: entity.label,
            [InternalNotificationKey.FILE_ID]: entity.data!.entityId
          });
        }
      });
    });
  }

  cancel() {
    this.form.reset();
    this.modalRef.close(null);
  }

  private getAllowableEntityTypes(): EntityType[] {
    const selectedEntityTypeFilter = this.entityTypeControl.value as EntityTypeFilterOptions;
    const selectedTemplate = this.availableTemplates.find(tmpl => tmpl.id === this.nameControl.value);

    if (selectedTemplate) {
      if (selectedEntityTypeFilter === EntityTypeFilterOptions.ALL) {
        return selectedTemplate.entityReferringTo;
      } else if (selectedTemplate.entityReferringTo.some(entity => String(entity) === selectedEntityTypeFilter)) {
        return [selectedEntityTypeFilter as unknown as EntityType];
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  protected readonly EntityType = EntityType;

}
